// Intensity grid derived from the EDITOR Draft (programEdit.ts) instead
// of a stored Program — the composer's live preview recolours on every
// block commit, mid-edit, so it can't go through buildPayload (which
// rejects incomplete drafts). Tolerant by design: an unparsable value or
// a value-less kind simply contributes no band, it never throws.
//
// Same band semantics as programIntensity.ts (the stored-Program twin):
// hardest set wins the (day, week) cell; %-of-MAX or effort both colour;
// `max` stays reserved for %-of-MAX ≥ 85.

import { parseFloatLoose, type Draft, type SetRowDraft } from "./programEdit.js";
import {
  BAND_RANK,
  bandOf,
  higherBand,
  rpeBand,
  rirBand,
  type IntensityBand,
  type IntensityGrid,
  type WeekCell,
} from "./programIntensity.js";

const fmt = (n: number): string => String(Math.round(n * 100) / 100);

// One set-row's contribution at week w. The row's kind decides the scale;
// absolute kg / none / history place nothing on it (same rule as the
// stored-Program derivation).
const rowCell = (row: SetRowDraft, w: number): Omit<WeekCell, "sets"> => {
  const v = parseFloatLoose(row.cells[w]?.intensityValue ?? "");
  if (v == null) return { band: null, value: null };
  let band: IntensityBand | null = null;
  switch (row.intensityKind) {
    case "percent-of-max":
      band = bandOf(v);
      break;
    case "rpe":
      band = rpeBand(v);
      break;
    case "rir":
      band = rirBand(v);
      break;
    default:
      return { band: null, value: null };
  }
  return band ? { band, value: fmt(v) } : { band: null, value: null };
};

// Same shape as buildIntensityGrid(program): one row per session,
// cycleWeeks columns, hardest banded set colours the cell. Slots opted
// out of a week (inactiveWeeks[w]) contribute nothing there.
export function buildIntensityGridFromDraft(draft: Draft): IntensityGrid {
  const weeks = draft.cycleWeeks;
  const days = draft.sessions.map((session, sessionIdx) => {
    const cells: WeekCell[] = [];
    for (let w = 0; w < weeks; w += 1) {
      let cell: Omit<WeekCell, "sets"> = { band: null, value: null };
      let sets = 0;
      for (const slot of session.slots) {
        if (slot.inactiveWeeks[w]) continue;
        sets += slot.setRows.length;
        for (const row of slot.setRows) {
          const c = rowCell(row, w);
          if (c.band && BAND_RANK[c.band] > (cell.band ? BAND_RANK[cell.band] : 0)) {
            cell = c;
          }
        }
      }
      cells.push({ ...cell, sets });
    }
    const band = cells.reduce<IntensityBand | null>((acc, c) => higherBand(acc, c.band), null);
    return { sessionIdx, name: session.name, cells, band };
  });
  return { weeks, days };
}
